import React from "react";
import { useLocation } from "react-router-dom";
import cn from "classnames";
import styles from "./gateways-list-row.module.scss";

import { IconButton } from "@components/atoms/buttons/icon-button/icon-button";
import arrow from "@src/assets/svg/arrow.svg";
import { GatewayMachinesList } from "@src/components/organisms/gateway-machines-list/gateway-machines-list";
import { GatewayMachinesListHeader } from "../gateway-machines-list-header/gateway-machines-list-header";
import gatewayIcon from "@src/assets/svg/gateway-icon.svg";
import { GatewayWithMachinesViewStateTypes } from "@src/types/gateways-with-machines";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import { useGatewaysListRowHook } from "./use-gateways-list-row-hook";
import { EditGatewayName } from "../edit-gateway-name/edit-gateway-name";
import { Button } from "@src/components/atoms/buttons/button/button";
import { RenderIfUserHasMultiplePermissions } from "../render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

interface GatewaysListRowPropTypes {
  gatewayData: GatewayWithMachinesViewStateTypes;
}

const cssClassName = "gateways-list-row";

export const GatewaysListRow = ({ gatewayData }: GatewaysListRowPropTypes) => {
  const location = useLocation();
  const targetGatewayUuid =
    new URLSearchParams(location.search).get("gateway_uuid")?.trim() || "";
  const { toggleSourcesVisibility, handleAddMachineClick, texts } =
    useGatewaysListRowHook(gatewayData);

  const handleCopyGatewayUuid = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(gatewayData.gateway_uuid);
      successSnackbar(texts.gatewayUuidCopied);
    } catch {
      errorSnackbar(texts.gatewayUuidCopyError);
    }
  };

  return (
    <tr
      key={gatewayData.gateway_uuid}
      id={`gateway-row-${gatewayData.gateway_uuid}`}
      className={cn(styles[`${cssClassName}__grid-row`], {
        [styles[`${cssClassName}__grid-row--open`]]:
          gatewayData.sources_visible && gatewayData.machines.length > 0,
        [styles[`${cssClassName}__grid-row--selected`]]:
          targetGatewayUuid === gatewayData.gateway_uuid,
      })}
    >
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.machines.length > 0,
        })}
      >
        <span
          className={cn(
            styles[`${cssClassName}__grid-cell-flex-wrapper`],
            styles[
              `${cssClassName}__grid-cell-flex-wrapper--gateway-name-container`
            ],
          )}
        >
          <span
            className={styles[`${cssClassName}__indicator-padding-wrapper`]}
          >
            {/* <IndicatorText text={""} variant={"green"} /> */}
          </span>
          <img src={gatewayIcon} alt={texts.gatewayIconAltText} />

          <EditGatewayName gateway={gatewayData} />

          <RenderIfUserHasMultiplePermissions
            permissions={["manage-gateways", "manage-sources"]}
            requireAll
          >
            <span className={styles[`${cssClassName}__no-shrink-wrapper`]}>
              <VerticalSeparator />
            </span>
            <span className={styles[`${cssClassName}__add-source-wrapper`]}>
              <Button
                fun={handleAddMachineClick}
                text={texts.addMachineText}
                variant="uppercase"
                tooltip={texts.addMachineTooltip}
              />
            </span>
          </RenderIfUserHasMultiplePermissions>

          <span
            className={cn(
              styles[`${cssClassName}__grid-cell-flex-wrapper`],
              styles[`${cssClassName}__grid-cell-flex-wrapper--no-gap`],
            )}
          >
            <span
              className={cn(styles[`${cssClassName}__sources-amount-text`])}
            >
              {gatewayData.machines.length}
            </span>
            {gatewayData.machines.length > 0 && (
              <span className={styles[`${cssClassName}__no-shrink-wrapper`]}>
                <IconButton
                  icon={arrow}
                  fun={toggleSourcesVisibility}
                  rotate={gatewayData.sources_visible}
                />
              </span>
            )}
          </span>
        </span>
      </td>
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.machines.length > 0,
        })}
      >
        <span className={styles[`${cssClassName}__description-text`]}>
          {gatewayData.vendor_id
            ? gatewayData.vendor_id
            : texts.noVendorIDAvailable}
        </span>
      </td>
      <td
        className={cn(styles[`${cssClassName}__grid-cell`], {
          [styles[`${cssClassName}__grid-cell--open`]]:
            gatewayData.sources_visible && gatewayData.machines.length > 0,
        })}
      >
        {gatewayData.gateway_uuid ? (
          <button
            type="button"
            className={cn(
              styles[`${cssClassName}__description-text`],
              styles[`${cssClassName}__gateway-uuid-copy-button`],
            )}
            aria-label={`${texts.copyGatewayUuid}: ${gatewayData.gateway_uuid}`}
            onClick={handleCopyGatewayUuid}
          >
            {gatewayData.gateway_uuid}
          </button>
        ) : null}
      </td>
      {gatewayData.machines.length > 0 && gatewayData.sources_visible && (
        <td className={styles[`${cssClassName}__sources-table-container`]}>
          <span
            className={styles[`${cssClassName}__sources-table-outer-wrapper`]}
          >
            <table className={styles[`${cssClassName}__table`]}>
              <GatewayMachinesListHeader />
              <GatewayMachinesList gatewayData={gatewayData} />
            </table>
          </span>
        </td>
      )}
    </tr>
  );
};
